import React from 'react';

const shimmerKeyframes = `
@keyframes module-skeleton-shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}`;

let stylesInjected = false;
function ensureStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = shimmerKeyframes;
  document.head.appendChild(style);
}

const Bar = ({ width, height = 14, style }) => (
  <div
    style={{
      width: width || '100%',
      minWidth: width || 200,
      height,
      background: '#e0e0e0',
      borderRadius: 4,
      position: 'relative',
      overflow: 'hidden',
      boxSizing: 'border-box',
      ...style,
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
        animation: 'module-skeleton-shimmer 1.5s infinite',
      }}
    />
  </div>
);

const Circle = ({ size = 18, style }) => (
  <div
    style={{
      width: size,
      minWidth: size,
      height: size,
      background: '#e0e0e0',
      borderRadius: '50%',
      position: 'relative',
      overflow: 'hidden',
      flexShrink: 0,
      ...style,
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
        animation: 'module-skeleton-shimmer 1.5s infinite',
      }}
    />
  </div>
);

// Use flex column layout to ensure children have width in flex parent contexts
const wrapperStyle = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
};

const ModuleSkeleton = ({ variant = 'field' }) => {
  ensureStyles();

  switch (variant) {
    // Label + input field
    case 'field':
      return (
        <div style={{ ...wrapperStyle, padding: '8px 0' }}>
          <Bar width={140} height={14} style={{ marginBottom: 8 }} />
          <Bar height={38} style={{ borderRadius: 4 }} />
        </div>
      );

    // Question text + 3 radio/checkbox option rows
    case 'question':
      return (
        <div style={{ ...wrapperStyle, padding: '8px 0' }}>
          <Bar width={280} height={16} style={{ marginBottom: 12 }} />
          {[180, 220, 150].map((w, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <Circle size={18} />
              <Bar width={w} height={14} />
            </div>
          ))}
        </div>
      );

    // Event card: checkbox + event details + price
    case 'event':
      return (
        <div style={{ ...wrapperStyle, padding: '10px 0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%' }}>
            <Bar width={20} height={20} style={{ borderRadius: 3, flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Bar width={220} height={16} style={{ marginBottom: 6 }} />
              <Bar width={160} height={12} style={{ marginBottom: 4 }} />
              <Bar width={300} height={12} />
            </div>
            <Bar width={70} height={16} style={{ flexShrink: 0 }} />
          </div>
        </div>
      );

    // Event group: title + subtitle + event list
    case 'event-group':
      return (
        <div style={{ ...wrapperStyle, padding: '8px 0' }}>
          <Bar width={250} height={20} style={{ marginBottom: 6 }} />
          <Bar width={180} height={14} style={{ marginBottom: 14 }} />
          {[1, 2].map(i => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', marginBottom: 10, paddingTop: 8, borderTop: '1px solid #eee' }}>
              <Bar width={20} height={20} style={{ borderRadius: 3, flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Bar width={200} height={14} style={{ marginBottom: 4 }} />
                <Bar width={140} height={12} />
              </div>
              <Bar width={60} height={14} style={{ flexShrink: 0 }} />
            </div>
          ))}
        </div>
      );

    // Image placeholder with aspect ratio
    case 'image':
      return (
        <div style={{ ...wrapperStyle }}>
          <Bar height={0} style={{ paddingBottom: '35%', borderRadius: 4, minWidth: '100%' }} />
        </div>
      );

    // Video player placeholder (16:9)
    case 'video':
      return (
        <div style={{ ...wrapperStyle }}>
          <div style={{
            width: '100%',
            paddingBottom: '56.25%',
            background: '#e0e0e0',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Play icon triangle */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderWidth: '20px 0 20px 36px',
              borderColor: 'transparent transparent transparent rgba(255,255,255,0.4)',
            }} />
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                animation: 'module-skeleton-shimmer 1.5s infinite',
              }}
            />
          </div>
        </div>
      );

    // Text content: multiple lines of varying width
    case 'text-content':
      return (
        <div style={{ ...wrapperStyle, padding: '8px 0' }}>
          {[
            { w: '100%', mw: 400 },
            { w: '100%', mw: 350 },
            { w: '100%', mw: 300 },
            { w: 250, mw: 250 },
          ].map((line, i) => (
            <Bar key={i} width={line.w} height={12} style={{ marginBottom: 6, minWidth: line.mw }} />
          ))}
        </div>
      );

    // Button (submit, forward, previous, generic)
    case 'button':
      return (
        <div style={{ ...wrapperStyle, padding: '8px 0', alignItems: 'flex-start' }}>
          <Bar width={140} height={40} style={{ borderRadius: 4 }} />
        </div>
      );

    // Order summary: header + table rows
    case 'order-summary':
      return (
        <div style={{ ...wrapperStyle, padding: '8px 0' }}>
          <Bar width={200} height={18} style={{ marginBottom: 12 }} />
          <div style={{ border: '1px solid #e0e0e0', borderRadius: 4, overflow: 'hidden', width: '100%', minWidth: 200 }}>
            <Bar height={36} style={{ borderRadius: 0, background: '#d5d5d5' }} />
            {[1, 2, 3].map(i => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderTop: '1px solid #eee' }}>
                <Bar width={180} height={14} />
                <Bar width={60} height={14} />
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', borderTop: '2px solid #d5d5d5' }}>
              <Bar width={80} height={16} />
              <Bar width={80} height={16} />
            </div>
          </div>
        </div>
      );

    // Payment options: select + button groups
    case 'payment':
      return (
        <div style={{ ...wrapperStyle, padding: '8px 0' }}>
          <Bar width={160} height={14} style={{ marginBottom: 8 }} />
          <Bar height={38} style={{ marginBottom: 16 }} />
          <Bar width={180} height={14} style={{ marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <Bar width={120} height={36} style={{ borderRadius: 4 }} />
            <Bar width={120} height={36} style={{ borderRadius: 4 }} />
          </div>
        </div>
      );

    // Discount: input with button
    case 'discount':
      return (
        <div style={{ ...wrapperStyle, padding: '8px 0' }}>
          <Bar width={140} height={14} style={{ marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 0, width: '100%', minWidth: 200 }}>
            <Bar height={38} style={{ flex: 1, borderRadius: '4px 0 0 4px', minWidth: 100 }} />
            <Bar width={80} height={38} style={{ borderRadius: '0 4px 4px 0', background: '#d0d0d0' }} />
          </div>
        </div>
      );

    // File uploader: upload area + file list
    case 'file-uploader':
      return (
        <div style={{ ...wrapperStyle, padding: '8px 0' }}>
          <Bar width={140} height={14} style={{ marginBottom: 8 }} />
          <div style={{
            width: '100%',
            minWidth: 200,
            height: 48,
            background: '#f5f5f5',
            border: '2px dashed #d0d0d0',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <Bar width={140} height={14} style={{ background: '#ddd' }} />
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                animation: 'module-skeleton-shimmer 1.5s infinite',
              }}
            />
          </div>
        </div>
      );

    // Hotel: radio options + search area
    case 'hotel':
      return (
        <div style={{ ...wrapperStyle, padding: '8px 0' }}>
          <Bar width={220} height={16} style={{ marginBottom: 12 }} />
          {[160, 140].map((w, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <Circle size={18} />
              <Bar width={w} height={14} />
            </div>
          ))}
          <div style={{ marginTop: 8, padding: 12, border: '1px solid #e0e0e0', borderRadius: 4, width: '100%', minWidth: 200, boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Bar width={130} height={38} style={{ borderRadius: 4 }} />
              <Bar width={130} height={38} style={{ borderRadius: 4 }} />
              <Bar width={100} height={38} style={{ borderRadius: 4 }} />
            </div>
          </div>
        </div>
      );

    // Group registration: button with icon
    case 'group-registration':
      return (
        <div style={{ ...wrapperStyle, padding: '8px 0', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Circle size={24} />
            <Bar width={160} height={36} style={{ borderRadius: 4 }} />
          </div>
        </div>
      );

    default:
      return (
        <div style={{ ...wrapperStyle, padding: '8px 0' }}>
          <Bar height={38} />
        </div>
      );
  }
};

export default ModuleSkeleton;
